/**
 * src/pages/HelpArticle.js
 *
 * Help guide detail page renders a single guide from embedded helpData.
 */

import React, { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  BookOpen,
  Compass,
  ShoppingBag,
  Heart,
  BarChart3,
  Award,
  Shield,
  ChevronRight,
} from "lucide-react";
import { getGuide, getGuides } from "../data/helpData";
import NoData from "../components/NoData";
import { errorDev } from '../utils/logger.js';

const CATEGORY_ICONS = {
  "getting-started": Compass,
  marketplace: ShoppingBag,
  "market-data": BarChart3,
  impact: Award,
  account: Shield,
};

export default function HelpArticle() {
  const { id } = useParams();
  const { t, i18n, ready: i18nReady } = useTranslation("support");
  const lang = i18n.language?.split("-")[0] || "en";

  const safeT = (key, fallback) => {
    if (!i18nReady) return fallback;
    try {
      const result = t(key);
      return result === key ? fallback : result;
    } catch (err) {
      errorDev("Operation failed:", err);
      return fallback;
    }
  };

  const guide = useMemo(() => getGuide(id, lang), [id, lang]);

  // Related guides: same category, exclude current
  const related = useMemo(() => {
    if (!guide) return [];
    return getGuides(lang, guide.category).filter((g) => g.id !== id).slice(0, 3);
  }, [guide, id, lang]);

  if (!guide) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex items-center justify-center px-4">
        <div className="text-center">
          <NoData
            title={safeT("guide_not_found", "This guide is not available.")}
            description=""
          />
          <Link
            to="/support"
            className="inline-flex items-center gap-1.5 mt-4 text-sm text-green-600 dark:text-green-400 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            {safeT("back_to_help", "Back to Help Center")}
          </Link>
        </div>
      </div>
    );
  }

  const Icon = CATEGORY_ICONS[guide.category] || BookOpen;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
      {/* Header */}
      <div className="bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
          <Link
            to="/support"
            className="inline-flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400 hover:underline mb-3"
          >
            <ArrowLeft className="w-4 h-4" />
            {safeT("back_to_help", "Back to Help Center")}
          </Link>

          <div className="flex items-start gap-3 mt-2">
            <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Icon className="w-5 h-5 text-green-700 dark:text-green-400" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                {guide.title}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {guide.summary}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 pb-24">
        {/* Guide Sections */}
        <div className="space-y-4">
          {guide.sections.map((section, idx) => (
            <article
              key={idx}
              className="bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-5"
            >
              <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-2">
                {section.heading}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                {section.body}
              </p>
            </article>
          ))}
        </div>

        {/* Related Guides */}
        {related.length > 0 && (
          <div className="mt-8">
            <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-3">
              {safeT("related_guides", "Related Guides")}
            </h2>
            <div className="space-y-2">
              {related.map((g) => {
                const RelIcon = CATEGORY_ICONS[g.category] || BookOpen;
                return (
                  <Link
                    key={g.id}
                    to={`/help/${g.id}`}
                    className="flex items-center gap-3 bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-3 hover:border-green-500 dark:hover:border-green-500 transition group"
                  >
                    <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                      <RelIcon className="w-4 h-4 text-green-700 dark:text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {g.title}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {g.summary}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-green-500 flex-shrink-0" />
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Contact CTA */}
        <div className="mt-8 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-bold text-gray-900 dark:text-white">
              {safeT("contact_cta", "Need help?")}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {safeT("contact_cta_desc", "Our support team is ready to assist you.")}
            </p>
          </div>
          <Link
            to="/support"
            onClick={() => {
              // Navigate to support with contact tab
              sessionStorage.setItem("supportTab", "contact");
            }}
            className="flex-shrink-0 px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            {safeT("contact_cta_button", "Contact Support")}
          </Link>
        </div>
      </div>
    </div>
  );
}
